import React, { useEffect, useMemo, useRef, useState } from "react";
import Fade from "@mui/material/Fade";
import type { TransitionProps } from "@mui/material/transitions";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import { usePickerContext } from "@mui/x-date-pickers/hooks";
import { pickersLayoutClasses } from "@mui/x-date-pickers/PickersLayout";
import type { PickersActionBarProps } from "@mui/x-date-pickers/PickersActionBar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TooltipButton } from "components/common/TooltipButton";
import { KeyActions, KeyFooter } from "components/main/utils/KeyActions";
import {
  createTheme,
  Theme,
  ThemeOptions,
  ThemeProvider,
  useTheme,
} from "@mui/material/styles";

interface DatePickerInputProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  format?: string;
  onError?: (reason: string | null) => void;
  disabled?: boolean;
  className?: string;
  /** Renders a calendar icon that opens the picker, named by this, in
   *  place of the typed field; `onChange` then hears only an accepted value. */
  iconLabel?: string;
  /** Picks a time of day too; `minDate` is then the earliest moment. Behind an
   *  icon the time sits in the footer, by Cancel / Apply. */
  withTime?: boolean;
}

const INPUT_HEIGHT = "32px";
// MUI fixes the week-day labels at this width, so a day cell must match to stay in its column.
const MUI_DAY_SIZE = "36px";
const BORDER_RADIUS = "var(--radius-app)";
const SLIDE_MS = 150;
const BORDER_COLOR = "var(--border-color)";
const ACCENT_COLOR = "var(--color-accent)";
const HOVER_BG = "var(--color-bg-hover)";
const SELECTED_BG = "var(--bg-accent-15)";

const INPUT_BASE_STYLES = {
  borderRadius: BORDER_RADIUS,
  padding: 0,
  backgroundColor: "var(--color-bg-subtle)",
  border: `1px solid ${BORDER_COLOR}`,
  borderWidth: "1px",
  "& fieldset": {
    display: "none",
    border: "none",
  },
  "&:hover": {
    backgroundColor: HOVER_BG,
    borderColor: BORDER_COLOR,
  },
  "&.Mui-focused": {
    backgroundColor: "var(--color-bg-subtle)",
    borderColor: ACCENT_COLOR,
    "& fieldset": {
      display: "none",
      border: "none",
    },
  },
};

const DISABLED_STYLES = { color: "var(--color-fg-muted)", opacity: 0.38 };

const BUTTON_CALENDAR_STYLES = {
  borderRadius: BORDER_RADIUS,
  borderWidth: "1px",
  border: "1px solid transparent",
  backgroundColor: "transparent",
  color: "var(--color-fg)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--font-size)",
  fontWeight: "var(--font-weight-regular)",
  minWidth: INPUT_HEIGHT,
  height: INPUT_HEIGHT,
  padding: 0,
  "&:hover": {
    backgroundColor: HOVER_BG,
  },
  "&.Mui-disabled:not(.Mui-selected)": DISABLED_STYLES,
  "&.Mui-selected": {
    backgroundColor: SELECTED_BG,
    color: ACCENT_COLOR,
    fontWeight: "var(--font-weight-regular)",
    borderColor: ACCENT_COLOR,
    "&:hover": {
      backgroundColor: SELECTED_BG,
      color: ACCENT_COLOR,
    },
    "&:focus": {
      backgroundColor: SELECTED_BG,
      color: ACCENT_COLOR,
    },
    "&.Mui-focusVisible": {
      backgroundColor: SELECTED_BG,
      color: ACCENT_COLOR,
    },
  },
  "& .MuiTouchRipple-root": {
    display: "none",
  },
};

function buildPickerTheme(theme: Theme) {
  return createTheme({
    ...theme,
    components: {
      ...theme.components,
      MuiPaper: {
        styleOverrides: {
          root: {
            "&.MuiPickerPopper-paper": {
              backgroundColor: "var(--color-bg-subtle)",
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${BORDER_COLOR}`,
              // Dark-mode Paper lays a white elevation overlay over any backgroundColor.
              backgroundImage: "none",
              boxShadow:
                "0 1px 2px var(--color-shadow-contact), 0 4px 12px -2px var(--color-shadow-ambient)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--font-size)",
              color: "var(--color-fg)",
              // It slides out of the field and back into it: from above when it opens below.
              "--picker-from": "translateY(-8px)",
              "[data-popper-placement^='top'] &": {
                "--picker-from": "translateY(8px)",
              },
              animation: `pickerSlideIn ${SLIDE_MS}ms ease-out`,
              "&[data-leaving]": {
                animation: `pickerSlideOut ${SLIDE_MS}ms ease-in forwards`,
              },
              "@keyframes pickerSlideIn": {
                from: { opacity: 0, transform: "var(--picker-from)" },
                to: { opacity: 1, transform: "none" },
              },
              "@keyframes pickerSlideOut": {
                from: { opacity: 1, transform: "none" },
                to: { opacity: 0, transform: "var(--picker-from)" },
              },
              // Not the time footer: its Cancel / Apply are the house's, dressed as everywhere.
              [`& *:not(.${pickersLayoutClasses.actionBar}, .${pickersLayoutClasses.actionBar} *)`]:
                {
                  fontFamily: "var(--font-sans)",
                  fontSize: "var(--font-size)",
                  fontWeight: "var(--font-weight-regular)",
                  color: "var(--color-fg)",
                },
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            minHeight: INPUT_HEIGHT,
            maxHeight: INPUT_HEIGHT,
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            "&.MuiPickersTextField-root": {
              height: INPUT_HEIGHT,
              minHeight: INPUT_HEIGHT,
              maxHeight: INPUT_HEIGHT,
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
            },
          },
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            ...INPUT_BASE_STYLES,
            height: `${INPUT_HEIGHT} !important`,
            minHeight: `${INPUT_HEIGHT} !important`,
            maxHeight: `${INPUT_HEIGHT} !important`,
            fontFamily: "var(--font-sans)",
            fontSize: "var(--font-size)",
            color: "var(--color-fg)",
            boxSizing: "border-box",
            margin: 0,
            "&.MuiPickersInputBase-adornedEnd": {
              height: `${INPUT_HEIGHT} !important`,
              minHeight: `${INPUT_HEIGHT} !important`,
              maxHeight: `${INPUT_HEIGHT} !important`,
              borderRadius: BORDER_RADIUS,
            },
          },
          input: {
            padding: "0 12px",
            fontWeight: "var(--font-weight-regular)",
          },
        },
      },
      MuiPickersSectionList: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            minHeight: INPUT_HEIGHT,
            maxHeight: INPUT_HEIGHT,
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            overflow: "hidden",
            width: "100%",
            padding: "0 12px",
            "& .MuiPickersSectionList-sectionContainer": {
              height: INPUT_HEIGHT,
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: INPUT_HEIGHT,
            },
            "& .MuiPickersSectionList-section": {
              height: INPUT_HEIGHT,
              display: "flex",
              alignItems: "center",
              lineHeight: INPUT_HEIGHT,
            },
            "& .MuiPickersSectionList-sectionContent": {
              lineHeight: INPUT_HEIGHT,
            },
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            width: "24px",
            height: "24px",
            minWidth: "24px",
            padding: 0,
            boxSizing: "border-box",
            "&:hover": {
              backgroundColor: HOVER_BG,
            },
            "&.Mui-selected": {
              backgroundColor: SELECTED_BG,
              color: ACCENT_COLOR,
            },
            "& svg": {
              width: "16px",
              height: "16px",
              fontSize: "16px",
            },
            "&.MuiIconButton-edgeEnd": {
              overflow: "visible",
            },
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          button: {
            borderRadius: BORDER_RADIUS,
            width: "24px",
            height: "24px",
            minWidth: "24px",
            padding: 0,
            "&.Mui-disabled": DISABLED_STYLES,
          },
        },
      },
      MuiPickerDay: {
        styleOverrides: {
          root: {
            ...BUTTON_CALENDAR_STYLES,
            height: MUI_DAY_SIZE,
            "&.MuiPickerDay-today": { outlineColor: ACCENT_COLOR },
          },
        },
      },
      MuiMonthCalendar: {
        styleOverrides: {
          button: {
            ...BUTTON_CALENDAR_STYLES,
          },
        },
      },
      MuiYearCalendar: {
        styleOverrides: {
          button: {
            ...BUTTON_CALENDAR_STYLES,
          },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-sans)",
            "& .MuiPickersCalendarHeader-label": {
              fontSize: "var(--font-size)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--color-fg)",
            },
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontWeight: "var(--font-weight-medium)",
          },
        },
      },
      MuiMultiSectionDigitalClockSection: {
        styleOverrides: {
          root: { borderColor: BORDER_COLOR },
          item: {
            ...BUTTON_CALENDAR_STYLES,
            width: "auto",
            margin: "2px 4px",
            fontVariantNumeric: "tabular-nums",
          },
        },
      },
      MuiMultiSectionDigitalClock: {
        styleOverrides: { root: { borderColor: BORDER_COLOR } },
      },
      MuiPickersLayout: {
        styleOverrides: {
          actionBar: {
            borderTop: `1px solid ${BORDER_COLOR}`,
            padding: "6px 8px",
            "& .MuiButton-root": {
              textTransform: "none",
              borderRadius: BORDER_RADIUS,
              minWidth: 0,
              padding: "2px 10px",
              color: "var(--color-fg)",
              "&:hover": { backgroundColor: HOVER_BG },
            },
            "& .MuiButton-root:last-of-type": { color: ACCENT_COLOR },
          },
        },
      },
      MuiPickersToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: "var(--color-bg-subtle)",
            borderBottom: `1px solid ${BORDER_COLOR}`,
            padding: "8px 12px",
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            maxHeight: INPUT_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: 0,
            "&.MuiInputAdornment-positionEnd": {
              paddingRight: "4px",
            },
          },
        },
      },
    } as ThemeOptions["components"],
  });
}

const CalendarTrigger: React.FC = () => {
  const picker = usePickerContext();
  const label = String(picker.label);
  return (
    <TooltipButton
      ref={picker.triggerRef}
      title={label}
      aria-label={label}
      variant="contextual"
      size="sm"
      disabled={picker.disabled}
      onClick={() => picker.setOpen((open) => !open)}
    >
      <CalendarTodayIcon sx={{ fontSize: 16 }} />
    </TooltipButton>
  );
};

const pad = (n: number) => String(n).padStart(2, "0");

/** A date and time behind an icon: the time under the days, beside Cancel and
 *  Apply, rather than as clock columns beside the calendar. */
const TimeFooter: React.FC<PickersActionBarProps & { earliest?: Date }> = ({
  earliest,
}) => {
  const picker = usePickerContext();
  const value = picker.value as Date | null;
  const accept = () => picker.acceptValueChanges();
  return (
    <KeyFooter className={pickersLayoutClasses.actionBar}>
      <input
        type="time"
        aria-label="Time"
        className="menu-trigger-sm time-input-reset w-[5.5rem]"
        value={
          value ? `${pad(value.getHours())}:${pad(value.getMinutes())}` : ""
        }
        onChange={(e) => {
          const [hours, minutes] = e.target.value.split(":").map(Number);
          if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
          const next = new Date(value ?? new Date());
          next.setHours(hours, minutes, 0, 0);
          picker.setValue(next, { changeImportance: "set" });
        }}
        onKeyDown={(e) => e.key === "Enter" && accept()}
      />
      <KeyActions
        className="ml-auto"
        confirmText="Apply"
        disabled={!value || (!!earliest && value < earliest)}
        onConfirm={accept}
        onCancel={() => picker.cancelValueChanges()}
      />
    </KeyFooter>
  );
};

export const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  format,
  onError,
  disabled,
  className = "",
  iconLabel,
  withTime = false,
}) => {
  const theme = useTheme();
  const datePickerTheme = useMemo(() => buildPickerTheme(theme), [theme]);
  const [open, setOpen] = useState(false);
  const field = useRef<HTMLDivElement>(null);

  // MUI closes on a `click` reaching the document, which a dialog's surface stops; a press is
  // caught on its way down instead.
  useEffect(() => {
    if (!open) return;
    const pressAway = (e: PointerEvent) => {
      const target = e.target as Element;
      if (
        !field.current?.contains(target) &&
        !target.closest(".MuiPickerPopper-root")
      )
        setOpen(false);
    };
    document.addEventListener("pointerdown", pressAway, true);
    return () => document.removeEventListener("pointerdown", pressAway, true);
  }, [open]);

  // The icon's own draft: a value that changed while the popper is open would
  // reflow the text beside the icon and the popper with it.
  const [draft, setDraft] = useState(value);
  const change = iconLabel
    ? { value: draft, onChange: setDraft, onAccept: onChange }
    : { value, onChange };
  const common = {
    ...change,
    ref: field,
    open,
    onOpen: () => {
      if (iconLabel) setDraft(value);
      setOpen(true);
    },
    onClose: () => setOpen(false),
    format,
    onError,
    disabled,
    className,
    label: iconLabel,
    slots: {
      ...(iconLabel ? { field: CalendarTrigger } : {}),
      desktopTransition: Fade as React.JSXElementConstructor<TransitionProps>,
    },
    slotProps: {
      textField: { required: true, size: "small" as const, fullWidth: true },
      // MUI's own modal layer (1300) sits under our dialogs.
      popper: { sx: { zIndex: "var(--z-menu)" } },
      // Fade only holds the calendar while it leaves; the keyframes above move it.
      desktopTransition: {
        timeout: SLIDE_MS,
        onExit: (node: HTMLElement) => node.setAttribute("data-leaving", ""),
      },
    },
    sx: iconLabel ? undefined : { width: "100%" },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={datePickerTheme}>
        {withTime && !iconLabel ? (
          <DesktopDateTimePicker
            {...common}
            minDateTime={minDate}
            maxDateTime={maxDate}
            ampm={false}
          />
        ) : withTime ? (
          <DesktopDatePicker
            {...common}
            minDate={minDate}
            maxDate={maxDate}
            // A day picked waits for its time and Apply.
            closeOnSelect={false}
            slots={{ ...common.slots, actionBar: TimeFooter }}
            slotProps={{
              ...common.slotProps,
              actionBar: { earliest: minDate } as never,
            }}
          />
        ) : (
          <DesktopDatePicker {...common} minDate={minDate} maxDate={maxDate} />
        )}
      </ThemeProvider>
    </LocalizationProvider>
  );
};
